import React, { useEffect, useState } from "react";
import { api, API, formatApiError } from "../lib/api";
import { FileText, Download, Loader2, BarChart3, Package, Euro, Users, Fuel, ReceiptText, UtensilsCrossed, BedDouble, Plane } from "lucide-react";
import { toast } from "sonner";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const TABS = [
  { key: "materiali", label: "Materiali consumati", icon: Package },
  { key: "trasferta", label: "Spese trasferta", icon: Plane },
];

export default function Reports() {
  const [tab, setTab] = useState("materiali");
  const [mese, setMese] = useState(currentMonth());
  const [commessaId, setCommessaId] = useState("");
  const [squadraId, setSquadraId] = useState("");
  const [commesse, setCommesse] = useState([]);
  const [squadre, setSquadre] = useState([]);
  const [report, setReport] = useState(null);
  const [trasferta, setTrasferta] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [com, sq] = await Promise.all([api.get("/commesse"), api.get("/squadre")]);
        setCommesse(com.data || []);
        setSquadre(sq.data || []);
      } catch { /* */ }
    })();
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      if (tab === "materiali") {
        const params = new URLSearchParams({ mese });
        if (commessaId) params.set("commessa_id", commessaId);
        const { data } = await api.get(`/reports/materiali-mensile?${params}`);
        setReport(data);
        setTrasferta(null);
      } else {
        const params = new URLSearchParams({ mese });
        if (squadraId) params.set("squadra_id", squadraId);
        const { data } = await api.get(`/reports/spese-trasferta-mensile?${params}`);
        setTrasferta(data);
        setReport(null);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = () => {
    const token = localStorage.getItem("access_token");
    let params;
    let path;
    if (tab === "materiali") {
      params = new URLSearchParams({ mese, formato: "pdf" });
      if (commessaId) params.set("commessa_id", commessaId);
      path = "/reports/materiali-mensile";
    } else {
      params = new URLSearchParams({ mese, formato: "xlsx" });
      if (squadraId) params.set("squadra_id", squadraId);
      path = "/reports/spese-trasferta-mensile";
    }
    if (token) params.set("auth", token);
    window.open(`${API}${path}?${params}`, "_blank");
  };

  const switchTab = (k) => { setTab(k); setReport(null); setTrasferta(null); };

  return (
    <div data-testid="reports-page">
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Reports</h1>
        <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
          Rendicontazione mensile · materiali consumati e spese trasferta
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b" style={{ borderColor: "var(--border)" }}>
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => switchTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 -mb-px border-b-2 text-sm font-semibold transition ${active ? "" : "hover:text-black/70"}`}
              style={{
                borderColor: active ? "var(--primary)" : "transparent",
                color: active ? "var(--primary)" : "var(--text-muted)",
              }}
              data-testid={`tab-${t.key}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="card-shell p-5 mb-6">
        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Mese</label>
            <input
              type="month" value={mese} onChange={(e) => setMese(e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid="report-mese"
            />
          </div>
          {tab === "materiali" ? (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Commessa</label>
              <select
                value={commessaId} onChange={(e) => setCommessaId(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
                style={{ borderColor: "var(--border)", background: "#fff" }}
                data-testid="report-commessa"
              >
                <option value="">Tutte le commesse</option>
                {commesse.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          ) : (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Squadra</label>
              <select
                value={squadraId} onChange={(e) => setSquadraId(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
                style={{ borderColor: "var(--border)", background: "#fff" }}
                data-testid="report-squadra"
              >
                <option value="">Tutte le squadre</option>
                {squadre.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          <div className="flex items-end gap-2 md:col-span-2">
            <button onClick={generate} disabled={loading} className="btn-primary" data-testid="report-generate">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />} Genera report
            </button>
            <button onClick={downloadFile} disabled={tab === "materiali" ? !report : !trasferta} className="btn-accent" data-testid="report-download">
              <Download className="w-4 h-4" /> {tab === "materiali" ? "Scarica PDF" : "Scarica Excel"}
            </button>
          </div>
        </div>
      </div>

      {tab === "materiali" && report && <MaterialiReport report={report} />}
      {tab === "trasferta" && trasferta && <TrasferteReport report={trasferta} />}
    </div>
  );
}

function MaterialiReport({ report }) {
  return (
    <div data-testid="report-output">
      <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: "Manrope" }}>
        {report.mese_label} {report.commessa ? `· ${report.commessa.name}` : "· Tutte le commesse"}
      </h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPI icon={Package} label="Pezzi totali" value={report.totali.qta_totale.toLocaleString("it-IT")} />
        <KPI icon={Euro} label="Valore totale" value={`€ ${report.totali.valore_totale.toLocaleString("it-IT", { maximumFractionDigits: 0 })}`} tone="accent" />
        <KPI icon={Users} label="Clienti" value={report.totali.clienti_count} />
        <KPI icon={FileText} label="Movimenti" value={report.totali.movimenti_count} />
      </div>

      <div className="card-shell overflow-hidden">
        <div className="p-4 border-b" style={{ borderColor: "var(--border)" }}>
          <h3 className="font-bold" style={{ fontFamily: "Manrope" }}>Dettaglio per prodotto</h3>
        </div>
        {report.aggregato.length === 0 ? (
          <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>
            Nessun materiale scaricato in questo periodo
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: "var(--surface-muted)" }}>
                <tr>
                  {["Categoria", "Prodotto", "SKU", "Q.tà", "Costo unit.", "Totale", "Clienti"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.aggregato.map((r) => (
                  <tr key={r.prodotto_id} className="border-t" style={{ borderColor: "var(--border)" }}>
                    <td className="px-4 py-3 text-xs capitalize">{r.categoria_label || r.categoria}</td>
                    <td className="px-4 py-3 font-semibold">{r.prodotto_nome}</td>
                    <td className="px-4 py-3 font-mono text-xs" style={{ color: "var(--text-muted)" }}>{r.prodotto_sku}</td>
                    <td className="px-4 py-3">{r.qta}</td>
                    <td className="px-4 py-3">€ {Number(r.costo_unitario).toFixed(2)}</td>
                    <td className="px-4 py-3 font-bold" style={{ color: "var(--primary)" }}>€ {Number(r.totale).toFixed(2)}</td>
                    <td className="px-4 py-3">{r.clienti_count}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2" style={{ borderColor: "var(--primary)" }}>
                  <td colSpan="3" className="px-4 py-3 text-right font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Totale</td>
                  <td className="px-4 py-3 font-bold">{report.totali.qta_totale}</td>
                  <td></td>
                  <td className="px-4 py-3 text-xl font-extrabold" style={{ color: "var(--accent)" }}>€ {report.totali.valore_totale.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function TrasferteReport({ report }) {
  const t = report.totali;
  return (
    <div data-testid="report-trasferta-output">
      <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: "Manrope" }}>
        {report.mese_label} {report.squadra_filtro ? `· ${report.squadra_filtro.name}` : "· Tutte le squadre"}
      </h2>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <KPI icon={Fuel} label="Carburante" value={`€ ${t.carburante.toFixed(2)}`} />
        <KPI icon={ReceiptText} label="Caselli" value={`€ ${t.caselli.toFixed(2)}`} />
        <KPI icon={UtensilsCrossed} label="Vitto" value={`€ ${t.vitto.toFixed(2)}`} />
        <KPI icon={BedDouble} label="Alloggio" value={`€ ${t.alloggio.toFixed(2)}`} />
        <KPI icon={Euro} label="TOTALE" value={`€ ${t.totale.toFixed(2)}`} tone="accent" />
      </div>

      {/* Riepilogo per squadra */}
      <div className="card-shell overflow-hidden mb-6">
        <div className="p-4 border-b" style={{ borderColor: "var(--border)" }}>
          <h3 className="font-bold" style={{ fontFamily: "Manrope" }}>Riepilogo per squadra · {report.riepilogo_squadre.length} squadre · {t.interventi} interventi</h3>
        </div>
        {report.riepilogo_squadre.length === 0 ? (
          <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>
            Nessun intervento pianificato in questo periodo
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="trasferta-riepilogo-table">
              <thead style={{ background: "var(--surface-muted)" }}>
                <tr>
                  {["Squadra", "Interventi", "Clienti", "Carburante", "Caselli", "Vitto", "Alloggio", "TOTALE"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.riepilogo_squadre.map((r) => (
                  <tr key={r.squadra_id || "nessuna"} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`riepilogo-squadra-${r.squadra_id || "none"}`}>
                    <td className="px-4 py-3 font-semibold">
                      <div className="inline-flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded-full" style={{ background: r.squadra_color }} />
                        {r.squadra_name}
                      </div>
                    </td>
                    <td className="px-4 py-3">{r.interventi}</td>
                    <td className="px-4 py-3">{r.clienti_count}</td>
                    <td className="px-4 py-3">€ {r.carburante.toFixed(2)}</td>
                    <td className="px-4 py-3">€ {r.caselli.toFixed(2)}</td>
                    <td className="px-4 py-3">€ {r.vitto.toFixed(2)}</td>
                    <td className="px-4 py-3">€ {r.alloggio.toFixed(2)}</td>
                    <td className="px-4 py-3 font-bold" style={{ color: "var(--primary)" }}>€ {r.totale.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2" style={{ borderColor: "var(--primary)" }}>
                  <td className="px-4 py-3 text-right font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Totale</td>
                  <td className="px-4 py-3 font-bold">{t.interventi}</td>
                  <td></td>
                  <td className="px-4 py-3 font-bold">€ {t.carburante.toFixed(2)}</td>
                  <td className="px-4 py-3 font-bold">€ {t.caselli.toFixed(2)}</td>
                  <td className="px-4 py-3 font-bold">€ {t.vitto.toFixed(2)}</td>
                  <td className="px-4 py-3 font-bold">€ {t.alloggio.toFixed(2)}</td>
                  <td className="px-4 py-3 text-xl font-extrabold" style={{ color: "var(--accent)" }}>€ {t.totale.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Dettaglio per cliente */}
      {report.dettaglio.length > 0 && (
        <div className="card-shell overflow-hidden">
          <div className="p-4 border-b" style={{ borderColor: "var(--border)" }}>
            <h3 className="font-bold" style={{ fontFamily: "Manrope" }}>Dettaglio per intervento ({report.dettaglio.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="trasferta-dettaglio-table">
              <thead style={{ background: "var(--surface-muted)" }}>
                <tr>
                  {["Data", "Squadra", "Cliente", "Città", "Km", "Carburante", "Caselli", "Vitto", "Alloggio", "TOTALE"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.dettaglio.map((r) => (
                  <tr key={r.pianificazione_id || r.cliente_id} className="border-t" style={{ borderColor: "var(--border)" }}>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">{(r.data_inizio || "").split("T")[0]}</td>
                    <td className="px-4 py-3 text-xs">
                      <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: r.squadra_color }} />
                      {r.squadra_name}
                    </td>
                    <td className="px-4 py-3 font-semibold">{r.cliente_nome} <span className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>({r.cliente_ref})</span></td>
                    <td className="px-4 py-3 text-xs">{r.cliente_citta}</td>
                    <td className="px-4 py-3 text-xs">{r.distanza_km != null ? `${r.distanza_km} km` : "—"}</td>
                    <td className="px-4 py-3">€ {r.carburante.toFixed(2)}</td>
                    <td className="px-4 py-3">€ {r.caselli.toFixed(2)}</td>
                    <td className="px-4 py-3">€ {r.vitto.toFixed(2)}</td>
                    <td className="px-4 py-3">€ {r.alloggio.toFixed(2)}</td>
                    <td className="px-4 py-3 font-bold" style={{ color: "var(--primary)" }}>€ {r.totale.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, tone = "primary" }) {
  const color = tone === "accent" ? "var(--accent)" : "var(--primary)";
  return (
    <div className="card-shell p-4 flex gap-3 items-center">
      <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: `${color}15`, color }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</div>
        <div className="metric-number" style={{ fontSize: "1.5rem" }}>{value}</div>
      </div>
    </div>
  );
}
